import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { EmailTemplatesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsQASubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import * as StandardEmailTemplateActions from "../../../actions/standardEmailTemplates";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { QALookupsLayout } from "../../index";

import "./QAEmailTemplatesPage.scss";

interface QAEmailTemplatesPageProps {
    formFilter: IFormFilter;
    standardEmailTemplates: Dtos.StandardEmailTemplate[];
    loadingStandardEmailTemplates: boolean;
    loadStandardEmailTemplatesSuccess: boolean;
    loadStandardEmailTemplatesFailure: boolean;
    loadStandardEmailTemplatesErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    clearStandardEmailTemplates: StandardEmailTemplateActions.IStandardEmailTemplateClearActionCreator;
    loadStandardEmailTemplates: StandardEmailTemplateActions.IStandardEmailTemplateLoadActionCreator;
    filter: StandardEmailTemplateActions.IStandardEmailTemplateFilterActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
}

const QAEmailTemplatesPage: FC<QAEmailTemplatesPageProps> = ({
    formFilter,
    standardEmailTemplates,
    loadingStandardEmailTemplates,
    loadStandardEmailTemplatesSuccess,
    loadStandardEmailTemplatesFailure,
    loadStandardEmailTemplatesErrorCode,
    permissions,
    pageSize,
    clearStandardEmailTemplates,
    loadStandardEmailTemplates,
    filter,
    setBreadcrumbs,
    setDrawerItems,
    setPageSize
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showStandardEmailTemplates =
        !loadingStandardEmailTemplates && loadStandardEmailTemplatesSuccess;


    // Load email templates
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsQASubMenu(permissions));
            setBreadcrumbs(EmailTemplatesBreadcrumbs());

            loadStandardEmailTemplates();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsQASubMenu,
        permissions,
        setBreadcrumbs,
        EmailTemplatesBreadcrumbs,
        loadStandardEmailTemplates,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadStandardEmailTemplatesFailure) {
            navigate(`/error/${loadStandardEmailTemplatesErrorCode}`);
        }
    }, [
        loadStandardEmailTemplatesFailure,
        loadStandardEmailTemplatesErrorCode,
        navigate,
    ]);

    // Cleanup effect to clear the email templates on component unmount
    useEffect(() => {
        return () => {
            clearStandardEmailTemplates();
        };
    }, [clearStandardEmailTemplates]);

    const createTable = useMemo(() => {
        if (!standardEmailTemplates || standardEmailTemplates.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no email templates to view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination = standardEmailTemplates.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={standardEmailTemplates}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination
                            ? pageSize
                            : standardEmailTemplates.length
                    }
                    pageSize={
                        showPagination
                            ? pageSize
                            : standardEmailTemplates.length
                    }
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no email templates to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string = `/settings/qa/qaemailtemplates/${rowInfo.original.id}`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "value",
                            Header: "Name",
                            accessor: "value",
                        },
                        {
                            id: "subject",
                            Header: "Subject",
                            accessor: "subject",
                        },
                        {
                            id: "body",
                            Header: "Body",
                            accessor: "body",
                        },
                    ]}
                />
            </div>
        );
    }, [
        standardEmailTemplates,
        pageSize,
        PAGE_SIZE_OPTIONS,
        setPageSize,
        navigate,
    ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>QA Email Templates</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />

                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.QAEmailTemplatesCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={"/settings/qa/qaemailtemplates/create"}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showStandardEmailTemplates ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showStandardEmailTemplates ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        formFilter,
        filter,
        permissions,
        showStandardEmailTemplates,
        createTable,
    ]);

    return (
        <QALookupsLayout
            loading={loadingStandardEmailTemplates}
            permission={Dtos.Permission.QAEmailTemplatesView}
        >
            {renderContent}
        </QALookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<QAEmailTemplatesPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];
    let allowedTypes: number[] = [1];
    let standardEmailTemplates: Dtos.StandardEmailTemplate[] | undefined =
        state.standardEmailTemplates.formData instanceof Array
            ? state.standardEmailTemplates.formData.filter((f) =>
                  allowedTypes.includes(f.standardEmailTypeId)
              )
            : undefined;

    return {
        formFilter: state.taskTypes.formFilter,
        standardEmailTemplates: standardEmailTemplates!,
        loadingStandardEmailTemplates:
            state.standardEmailTemplates.loadState &&
            state.standardEmailTemplates.loadState.status ==
                RequestState.Pending,
        loadStandardEmailTemplatesSuccess:
            state.standardEmailTemplates.loadState &&
            state.standardEmailTemplates.loadState.status ==
                RequestState.Success,
        loadStandardEmailTemplatesFailure:
            state.standardEmailTemplates.loadState &&
            state.standardEmailTemplates.loadState.status ==
                RequestState.Failure,
        loadStandardEmailTemplatesErrorCode:
            state.standardEmailTemplates.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (dispatch): Partial<QAEmailTemplatesPageProps> => {
    return {
        loadStandardEmailTemplates: bindActionCreators(
            StandardEmailTemplateActions.LoadStandardEmailTemplate,
            dispatch
        ),
        clearStandardEmailTemplates: bindActionCreators(
            StandardEmailTemplateActions.Clear,
            dispatch
        ),
        filter: bindActionCreators(
            StandardEmailTemplateActions.Filter,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(QAEmailTemplatesPage);
